/**
 * Карта. Ни одного hex и ни одного --p-*: только роли темы.
 *
 * Переворот в три фазы — это и есть подпись приложения в движении.
 * Одна линейная анимация читается как «флипнули тайл»; три фазы читаются
 * как «перевернули что-то тяжёлое».
 */

.card {
  --card-w: var(--card-w-lg);

  position: relative;
  display: block;
  width: var(--card-w);
  aspect-ratio: var(--card-ratio);
  /* Перспектива на внешнем элементе: если поставить её на вращающийся,
     карта будет не поворачиваться, а плющиться. */
  perspective: 1200px;
  border-radius: var(--r-card);
  box-shadow: var(--c-card-mat, none);
  padding: 0;
  border: none;
  background: none;
  transition:
    transform var(--dur-base) var(--ease-out-soft),
    opacity var(--dur-base) var(--ease-standard);
}

.interactive {
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.inner {
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  /* Фаза 2 из трёх — сам поворот. Первая и третья заданы в keyframes ниже. */
  transition: transform var(--dur-slower) var(--ease-mystic);
}

.revealed .inner {
  transform: rotateY(180deg);
}

.back,
.face {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: var(--r-card);
  /* fill, а не cover: пропорция рубашки отличается от лица на 4%.
     Обрезать нельзя — скруглённые углы запечены в картинку, cover срежет их
     и оставит прямые углы посреди скруглённой карты. Растяжение на 4%
     симметричного линеарта не читается. */
  object-fit: fill;
  user-select: none;
  -webkit-user-drag: none;
}

.face {
  transform: rotateY(180deg);
  object-fit: cover;
}

/* Перевёрнутая карта: разворачиваем только рисунок, но не саму карту —
   иначе вместе с ним развернутся рамка, свечение и значок. */
.upsideDown {
  transform: rotateY(180deg) rotate(180deg);
}

/* ── Слои поверх картинки ──────────────────────────────────────────────
   Отдельные элементы, чтобы их анимация не перерисовывала изображение. */

.frame {
  position: absolute;
  inset: 0;
  border-radius: var(--r-card);
  box-shadow: inset 0 0 0 1px var(--c-border-sacred);
  pointer-events: none;
  transition: box-shadow var(--dur-base) var(--ease-standard);
}

.glow {
  position: absolute;
  inset: 0;
  border-radius: var(--r-card);
  pointer-events: none;
  opacity: 0;
  transition: opacity var(--dur-slow) var(--ease-standard);
  box-shadow: var(--sh-glow-card);
}

/**
 * Лак — общая оправа всех 78 карт.
 *
 * Второй по силе склеиватель набора после грейда (см. web/scripts/grade-cards.sh).
 * Грейд делает иллюстрации одной плёнкой, лак делает их одним предметом:
 * одинаковая виньетка, одинаковая фаска, одинаковый блик сверху — у всех,
 * без исключений. Ровно то, чем держатся карточные интерфейсы, где сотни
 * разных иллюстраций читаются одним набором.
 *
 * Виньетка здесь, а не в запечённом файле, намеренно: она одинакова для всех
 * карт, значит запекать её 78 раз — это 78 копий одного градиента. И в светлой
 * теме она обязана стать светлее, чего запечённый файл не умеет.
 */
.lacquer {
  position: absolute;
  inset: 0;
  border-radius: var(--r-card);
  pointer-events: none;
  background: radial-gradient(
    ellipse 76% 60% at 50% 44%,
    transparent 54%,
    var(--c-atm-vignette) 100%
  );
  box-shadow:
    inset 0 1px 0 var(--c-mat-bevel-hi),
    inset 0 -1px 0 var(--c-mat-bevel-lo);
}

/**
 * Фольга. Только карта дня — и это правило, а не рекомендация.
 *
 * Голография работает ровно до тех пор, пока она редкость. Радужный перелив
 * на всех 78 картах в сетке колоды превращает дорогой приём в наклейку
 * с рынка. Поэтому проп holo существует, но по умолчанию выключен.
 *
 * Два слоя, потому что у настоящей фольги их два: сама плёнка (переливается
 * от угла зрения) и блик на лаке (следует за источником света). Один слой
 * без другого читается как цветной фильтр, а не как материал.
 */
.holo,
.glare {
  position: absolute;
  inset: 0;
  border-radius: var(--r-card);
  pointer-events: none;
  opacity: var(--holo-strength, 0);
  transition: opacity var(--dur-slow) var(--ease-out-soft);
}

.holo {
  background: var(--c-mat-holo);
  /* color-dodge, а не screen: dodge поднимает только уже светлые места
     и оставляет тени нетронутыми — фольга ложится на рельеф картинки,
     а не поверх неё ровным слоем. */
  mix-blend-mode: color-dodge;
}

.glare {
  background: radial-gradient(
    circle 42% at var(--holo-x, 50%) var(--holo-y, 50%),
    var(--c-mat-specular),
    transparent 68%
  );
  mix-blend-mode: overlay;
  opacity: calc(var(--holo-strength, 0) * 0.55);
}

/* Родителю нужна собственная зона смешивания: без неё mix-blend-mode
   уедет смешиваться с фоном страницы, а не с картинкой карты. */
.holoHost {
  isolation: isolate;
}

/* ── Состояния ─────────────────────────────────────────────────────────*/

.selected {
  transform: translateY(-8px);
}

.selected .frame {
  box-shadow: inset 0 0 0 1px var(--c-sacred);
}

.selected .glow {
  opacity: 1;
}

/* Приглушение прозрачностью, а не grayscale: обесцвеченная карта читается
   как «картинка не загрузилась». */
.dimmed {
  opacity: 0.42;
}

.interactive:active:not(.dimmed) {
  transform: scale(0.97);
}

.selected.interactive:active {
  transform: translateY(-8px) scale(0.97);
}

.loading .inner::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: var(--r-card);
  background: linear-gradient(
    100deg,
    transparent 30%,
    var(--c-rim-light) 50%,
    transparent 70%
  );
  background-size: 220% 100%;
  animation: shimmer var(--dur-ritual) var(--ease-standard) infinite;
}

@keyframes shimmer {
  from { background-position: 180% 0; }
  to { background-position: -80% 0; }
}

/* Значок перевёрнутой карты. Холодный цвет — единственный в системе:
   это не ошибка и не награда, это другой регистр. */
.reversedMark {
  position: absolute;
  top: 4%;
  right: 4%;
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: var(--r-pill);
  background: var(--c-arcane-bg);
  color: var(--c-arcane);
  font: var(--t-caption);
  backdrop-filter: blur(4px);
}

/* Мелкие размеры: детали не читаются, лишние слои только шумят. */
.card:where([style*='--card-w-xs'], [style*='--card-w-sm']) .reversedMark {
  display: none;
}

/**
 * Типизация переменной угла.
 *
 * Без @property браузер считает --holo-angle строкой и не умеет её
 * интерполировать — перелив идёт ступеньками. С inherits: false правка
 * переменной пересчитывает стиль только этого элемента, а не всего
 * поддерева под ним.
 *
 * Браузеры без поддержки @property просто игнорируют блок, и переменная
 * работает как обычная нетипизированная. Фолбэк не нужен.
 */
@property --holo-angle {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}

@property --holo-strength {
  syntax: '<number>';
  inherits: false;
  initial-value: 0;
}

/**
 * Свет по карте в момент раскрытия.
 *
 * Один проход блика ровно тогда, когда карта повернулась лицом. Это и есть
 * та точка, ради которой всё затевалось: не «появилась картинка», а
 * «по предмету прошёл свет». Дальше карта лежит спокойно — постоянно
 * бегущий блик превращает предмет в баннер.
 */
.revealed .sweep {
  position: absolute;
  inset: 0;
  border-radius: var(--r-card);
  pointer-events: none;
  background: linear-gradient(
    104deg,
    transparent 40%,
    var(--c-mat-specular) 50%,
    transparent 60%
  );
  mix-blend-mode: overlay;
  animation: card-sweep var(--dur-ritual) var(--ease-out-soft) var(--dur-slower) both;
}

@keyframes card-sweep {
  from { opacity: 0; transform: translate3d(-46%, 0, 0); }
  35% { opacity: 1; }
  to { opacity: 0; transform: translate3d(46%, 0, 0); }
}

/* В спокойном режиме карта раскрывается без блика: приём разовый,
   и его отсутствие не читается как потеря. */
[data-motion='calm'] .revealed .sweep {
  animation: none;
  opacity: 0;
}
